/**
 * Comprehensive validation for Google Flights Fee Rules
 * Based on the official "Price through Google" documentation
 */

// Valid IATA airline codes are 2-letter codes
const CARRIER_PATTERN = /^[A-Z0-9]{2}$/;
const CARRIER_GROUP_PATTERN = /^U:\d+$/;
const ALL_CARRIERS = '**';

// Location patterns
const AIRPORT_PATTERN = /^P:[A-Z]{3}$/;
const CITY_PATTERN = /^C:[A-Z]{3}$/;
const COUNTRY_PATTERN = /^N:[A-Z]{2}$/;
const LOCATION_GROUP_PATTERN = /^U:\d+$/;

// Price pattern: CURRENCY_CODE followed by amount (may be negative)
const PRICE_PATTERN = /^[A-Z]{3}-?\d+(\.\d{1,2})?$/;

// Fee amount pattern
const FEE_AMOUNT_PATTERN = /^[A-Z]{3}-?\d+(\.\d{1,2})?$/;

const VALID_TRIP_TYPES = ['ONE_WAY', 'ROUND_TRIP', 'OPEN_JAW', 'OTHER', 'ANY'];
const VALID_CABIN_CLASSES = ['ECONOMY', 'PREMIUM', 'BUSINESS', 'FIRST', 'ANY'];
const VALID_CABIN_MATCH_MODES = ['LONGEST_SEGMENTS', 'ALL_SEGMENTS', 'ANY_SEGMENT'];
const VALID_PASSENGER_TYPES = ['CHILD', 'ANY'];
const VALID_PRICE_SOURCES = ['GDS', 'WEBSITE', 'ANY'];

export function validateCarrier(carrier) {
  if (!carrier) return 'Carrier is required';
  if (carrier === ALL_CARRIERS) return null;
  if (CARRIER_GROUP_PATTERN.test(carrier)) return null;
  if (CARRIER_PATTERN.test(carrier)) return null;
  return `Invalid carrier "${carrier}". Use 2-letter IATA code, "**" for all, or "U:N" for carrier group`;
}

export function validateLocation(location, fieldName) {
  if (!location) return null; // Optional field
  if (AIRPORT_PATTERN.test(location)) return null;
  if (CITY_PATTERN.test(location)) return null;
  if (COUNTRY_PATTERN.test(location)) return null;
  if (LOCATION_GROUP_PATTERN.test(location)) return null;
  return `Invalid ${fieldName} "${location}". Use P:XXX (airport), C:XXX (city), N:XX (country), or U:N (group)`;
}

export function validateFeeAmount(amount) {
  if (!amount) return null;
  if (FEE_AMOUNT_PATTERN.test(amount)) return null;
  return `Invalid fee amount "${amount}". Format: CURRENCY_CODE + AMOUNT (e.g. USD5.00, CHF-1.05)`;
}

export function validateFeePercent(percent) {
  if (percent === null || percent === undefined || percent === '') return null;
  const num = parseFloat(percent);
  if (isNaN(num)) return 'Fee percent must be a number';
  return null;
}

export function validateDate(dateStr) {
  if (!dateStr) return null;
  const pattern = /^\d{4}-\d{2}-\d{2}$/;
  if (!pattern.test(dateStr)) return `Invalid date format "${dateStr}". Use YYYY-MM-DD`;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return `Invalid date "${dateStr}"`;
  return null;
}

export function validateFeeRule(rule, allRules = [], locationGroups = [], carrierGroups = []) {
  const errors = [];

  // Required fields
  if (!rule.carrier) errors.push('Carrier is required');
  else {
    const carrierErr = validateCarrier(rule.carrier);
    if (carrierErr) errors.push(carrierErr);
  }

  if (!rule.sequenceNum && rule.sequenceNum !== 0) {
    errors.push('Sequence number is required');
  } else if (rule.sequenceNum < 1) {
    errors.push('Sequence number must be positive');
  }

  // Validate origin/destination
  const originErr = validateLocation(rule.origin, 'origin');
  if (originErr) errors.push(originErr);

  const destErr = validateLocation(rule.destination, 'destination');
  if (destErr) errors.push(destErr);

  // Validate location group references
  if (rule.origin && LOCATION_GROUP_PATTERN.test(rule.origin)) {
    const groupNum = rule.origin.split(':')[1];
    if (locationGroups.length > 0 && !locationGroups.find(g => g.tableNum === groupNum)) {
      errors.push(`Origin references location group #${groupNum} which does not exist`);
    }
  }
  if (rule.destination && LOCATION_GROUP_PATTERN.test(rule.destination)) {
    const groupNum = rule.destination.split(':')[1];
    if (locationGroups.length > 0 && !locationGroups.find(g => g.tableNum === groupNum)) {
      errors.push(`Destination references location group #${groupNum} which does not exist`);
    }
  }

  // Validate carrier group references
  if (rule.carrier && CARRIER_GROUP_PATTERN.test(rule.carrier)) {
    const groupNum = rule.carrier.split(':')[1];
    if (carrierGroups.length > 0 && !carrierGroups.find(g => g.tableNum === groupNum)) {
      errors.push(`Carrier references carrier group #${groupNum} which does not exist`);
    }
  }

  // Fee type validations - must have exactly one fee type
  const hasFeeAmount = !!rule.feeAmount;
  const hasFeePercent = rule.feePercent !== null && rule.feePercent !== undefined && rule.feePercent !== '';
  const hasOncePerBooking = !!rule.oncePerBookingFeeAmount;
  const hasSuppress = rule.suppress === 'true' || rule.suppress === true;

  const feeTypeCount = [hasFeeAmount || hasFeePercent, hasOncePerBooking, hasSuppress].filter(Boolean).length;

  if (feeTypeCount === 0) {
    errors.push('Rule must have at least one fee type: FeeAmount, FeePercent, OncePerBookingFeeAmount, or Suppress');
  }

  // Suppress cannot be combined with fees
  if (hasSuppress && (hasFeeAmount || hasFeePercent || hasOncePerBooking)) {
    errors.push('Suppress cannot be combined with FeeAmount, FeePercent, or OncePerBookingFeeAmount');
  }

  // OncePerBookingFeeAmount cannot be combined with others
  if (hasOncePerBooking && (hasFeeAmount || hasFeePercent)) {
    errors.push('OncePerBookingFeeAmount cannot be combined with FeeAmount or FeePercent');
  }

  // Validate fee values
  if (hasFeeAmount) {
    const err = validateFeeAmount(rule.feeAmount);
    if (err) errors.push(err);
  }

  if (hasFeePercent) {
    const err = validateFeePercent(rule.feePercent);
    if (err) errors.push(err);
  }

  if (hasOncePerBooking) {
    const err = validateFeeAmount(rule.oncePerBookingFeeAmount);
    if (err) errors.push(err);
    // OncePerBookingFeeAmount must be positive
    if (rule.oncePerBookingFeeAmount) {
      const match = rule.oncePerBookingFeeAmount.match(/^[A-Z]{3}(-?\d+(\.\d{1,2})?)$/);
      if (match && parseFloat(match[1]) < 0) {
        errors.push('OncePerBookingFeeAmount must be positive (no discounts allowed)');
      }
    }
  }

  // Validate enums
  if (rule.tripType && !VALID_TRIP_TYPES.includes(rule.tripType)) {
    errors.push(`Invalid trip type "${rule.tripType}". Must be: ${VALID_TRIP_TYPES.join(', ')}`);
  }

  if (rule.cabinClass && !VALID_CABIN_CLASSES.includes(rule.cabinClass)) {
    errors.push(`Invalid cabin class "${rule.cabinClass}". Must be: ${VALID_CABIN_CLASSES.join(', ')}`);
  }

  if (rule.cabinClassMatchMode && !VALID_CABIN_MATCH_MODES.includes(rule.cabinClassMatchMode)) {
    errors.push(`Invalid cabin class match mode "${rule.cabinClassMatchMode}". Must be: ${VALID_CABIN_MATCH_MODES.join(', ')}`);
  }

  if (rule.passengerType && !VALID_PASSENGER_TYPES.includes(rule.passengerType)) {
    errors.push(`Invalid passenger type "${rule.passengerType}". Must be: ${VALID_PASSENGER_TYPES.join(', ')}`);
  }

  if (rule.priceSource && !VALID_PRICE_SOURCES.includes(rule.priceSource)) {
    errors.push(`Invalid price source "${rule.priceSource}". Must be: ${VALID_PRICE_SOURCES.join(', ')}`);
  }

  // Validate dates
  ['travelDateEffective', 'travelDateDiscontinue', 'ticketingDateEffective', 'ticketingDateDiscontinue'].forEach(field => {
    if (rule[field]) {
      const err = validateDate(rule[field]);
      if (err) errors.push(err);
    }
  });

  // Travel date logic: effective must be before discontinue
  if (rule.travelDateEffective && rule.travelDateDiscontinue) {
    if (new Date(rule.travelDateEffective) >= new Date(rule.travelDateDiscontinue)) {
      errors.push('TravelDateEffective must be before TravelDateDiscontinue');
    }
  }

  if (rule.ticketingDateEffective && rule.ticketingDateDiscontinue) {
    if (new Date(rule.ticketingDateEffective) >= new Date(rule.ticketingDateDiscontinue)) {
      errors.push('TicketingDateEffective must be before TicketingDateDiscontinue');
    }
  }

  // Validate price ranges
  if (rule.minPrice) {
    if (!PRICE_PATTERN.test(rule.minPrice)) {
      errors.push(`Invalid MinPrice "${rule.minPrice}". Format: CURRENCY_CODE + VALUE (e.g. USD100)`);
    }
  }
  if (rule.maxPrice) {
    if (!PRICE_PATTERN.test(rule.maxPrice)) {
      errors.push(`Invalid MaxPrice "${rule.maxPrice}". Format: CURRENCY_CODE + VALUE (e.g. USD500)`);
    }
  }

  // MinPrice and MaxPrice currency must match
  if (rule.minPrice && rule.maxPrice) {
    const minCurrency = rule.minPrice.substring(0, 3);
    const maxCurrency = rule.maxPrice.substring(0, 3);
    if (minCurrency !== maxCurrency) {
      errors.push(`MinPrice currency (${minCurrency}) must match MaxPrice currency (${maxCurrency})`);
    }
    const minVal = parseFloat(rule.minPrice.substring(3));
    const maxVal = parseFloat(rule.maxPrice.substring(3));
    if (minVal > maxVal) {
      errors.push('MinPrice must be less than or equal to MaxPrice');
    }
  }

  // Price ranges cannot be combined with OncePerBookingFeeAmount
  if ((rule.minPrice || rule.maxPrice) && hasOncePerBooking) {
    errors.push('MinPrice/MaxPrice cannot be combined with OncePerBookingFeeAmount');
  }

  // MinSegments and MinTotalSegments cannot be combined
  if (rule.minSegments && rule.minTotalSegments) {
    errors.push('MinSegments and MinTotalSegments cannot be used in the same rule');
  }

  // Validate integer fields
  if (rule.minSegments !== undefined && rule.minSegments !== null && rule.minSegments !== '') {
    if (parseInt(rule.minSegments) < 0) errors.push('MinSegments must be non-negative');
  }
  if (rule.minTotalSegments !== undefined && rule.minTotalSegments !== null && rule.minTotalSegments !== '') {
    if (parseInt(rule.minTotalSegments) < 0) errors.push('MinTotalSegments must be non-negative');
  }

  return errors;
}

export function validateRuleSet(ruleSet) {
  const errors = [];

  // Validate location groups
  const locationTableNums = new Set();
  (ruleSet.locationGroups || []).forEach((group, idx) => {
    if (!group.tableNum) {
      errors.push(`Location group #${idx + 1}: TableNum is required`);
    } else if (locationTableNums.has(group.tableNum)) {
      errors.push(`Duplicate location group TableNum: ${group.tableNum}`);
    } else {
      locationTableNums.add(group.tableNum);
    }

    if (!group.locations || group.locations.length === 0) {
      errors.push(`Location group #${group.tableNum || idx + 1}: Must have at least one location`);
    }

    (group.locations || []).forEach(loc => {
      if (!AIRPORT_PATTERN.test(loc) && !CITY_PATTERN.test(loc) && !COUNTRY_PATTERN.test(loc)) {
        errors.push(`Location group #${group.tableNum || idx + 1}: Invalid location "${loc}"`);
      }
    });
  });

  // Validate carrier groups
  const carrierTableNums = new Set();
  (ruleSet.carrierGroups || []).forEach((group, idx) => {
    if (!group.tableNum) {
      errors.push(`Carrier group #${idx + 1}: TableNum is required`);
    } else if (carrierTableNums.has(group.tableNum)) {
      errors.push(`Duplicate carrier group TableNum: ${group.tableNum}`);
    } else {
      carrierTableNums.add(group.tableNum);
    }

    if (!group.carriers || group.carriers.length === 0) {
      errors.push(`Carrier group #${group.tableNum || idx + 1}: Must have at least one carrier`);
    }

    (group.carriers || []).forEach(carrier => {
      if (!CARRIER_PATTERN.test(carrier)) {
        errors.push(`Carrier group #${group.tableNum || idx + 1}: Invalid carrier code "${carrier}"`);
      }
    });
  });

  // Validate fee rules
  const seqNums = new Set();
  let hasOncePerBooking = false;
  let hasNegativeFee = false;

  (ruleSet.feeRules || []).forEach(rule => {
    if (seqNums.has(rule.sequenceNum)) {
      errors.push(`Duplicate sequence number: ${rule.sequenceNum}`);
    } else {
      seqNums.add(rule.sequenceNum);
    }

    if (rule.oncePerBookingFeeAmount) hasOncePerBooking = true;

    if (rule.feeAmount) {
      const match = rule.feeAmount.match(/^[A-Z]{3}(-?\d+(\.\d{1,2})?)$/);
      if (match && parseFloat(match[1]) < 0) hasNegativeFee = true;
    }
    if (rule.feePercent && parseFloat(rule.feePercent) < 0) hasNegativeFee = true;

    const ruleErrors = validateFeeRule(rule, ruleSet.feeRules, ruleSet.locationGroups, ruleSet.carrierGroups);
    ruleErrors.forEach(err => errors.push(`Rule #${rule.sequenceNum}: ${err}`));
  });

  // OncePerBookingFeeAmount can only be used if no negative fees exist
  if (hasOncePerBooking && hasNegativeFee) {
    errors.push('OncePerBookingFeeAmount can only be used if no other rules contain negative fees (discounts)');
  }

  return errors;
}

export function validateLocationId(id) {
  if (!id) return 'Location ID is required';
  if (AIRPORT_PATTERN.test(id)) return null;
  if (CITY_PATTERN.test(id)) return null;
  if (COUNTRY_PATTERN.test(id)) return null;
  return 'Invalid location. Use P:XXX (airport), C:XXX (city), or N:XX (country)';
}

export function validateCarrierId(id) {
  if (!id) return 'Carrier ID is required';
  if (CARRIER_PATTERN.test(id)) return null;
  return 'Invalid carrier code. Must be a 2-letter IATA code (e.g. LH, AA)';
}
